/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, ReactElement, MouseEvent } from 'react';
import { useParams } from 'react-router';
import { useFormik } from 'formik';
import {
    Button,
    DrawerHeader,
    DrawerHeaderTitle,
    InlineDrawer,
    InlineDrawerProps,
    makeStyles,
    mergeClasses,
    SelectTabData,
    SelectTabEvent,
    Tab,
    TabList,
    TabValue,
    tokens,
    Tooltip,
    useRestoreFocusSource,
} from '@fluentui/react-components';
import {
    bundleIcon,
    CommentFilled,
    CommentRegular,
    Dismiss20Regular,
    DocumentBulletListFilled,
    DocumentBulletListRegular,
    LinkFilled,
    LinkRegular,
} from '@fluentui/react-icons';
import { Column, Row } from '../../../common';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { DocumentInfoPanel } from './DocumentInfoPanel';
import { DocumentLinksPanel } from './DocumentLinksPanel';
import { DocumentNotesPanel } from './DocumentNotesPanel';
import { CaseDocument } from '../../../../data/types';
import { useValidation } from '../../../../data/types/validation';

type CommonTooltipProps = {
    content: string;
    children: ReactElement;
};
const CommonTooltip = (props: CommonTooltipProps) => {
    const { content, children } = props;
    return (
        <Tooltip relationship='label' positioning='before-top' content={content}>
            {children}
        </Tooltip>
    );
};
enum TabNames {
    Info = 'Information',
    Note = 'Note',
    Task = 'Task',
    Links = 'Links',
}

const useStyles = makeStyles({
    drawer: {
        width: '320px',
    },
    toolColumn: {
        borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
        paddingInlineStart: '.5rem',
        height: '100%',
    },
    panelDataColumn: {
        paddingInlineEnd: '.5rem',
    },
    panelRow: {
        height: '100%',
        paddingBlock: '0rem',
    },
    header: {
        paddingInlineStart: '0rem',
        paddingBlockStart: '0rem',
    },
});

type DocumentDrawerProps = InlineDrawerProps & {
    setOpen: (open: boolean) => void;
    selectedDoc?: CaseDocument;
    onSubmit?: (values: CaseDocument) => Promise<void>;
    className?: string;
};

export const DocumentDrawer = (props: DocumentDrawerProps) => {
    const { setOpen, selectedDoc, onSubmit, ...otherProps } = props;

    //Params
    const { caseid } = useParams();

    //Hooks
    const restoreFocusSourceAttributes = useRestoreFocusSource();
    const { validateCaseDocument } = useValidation();

    //State
    const [selectedTab, setSelectedTab] = useState<TabValue>(TabNames.Info);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const tabClasses = mergeClasses(commonStyles.button.root);

    //Form stuff
    const caseDocForm = useFormik<CaseDocument>({
        initialValues: { ...selectedDoc },
        onSubmit: async (values, { resetForm }) => {
            if (!!(values as CaseDocument).name) {
                !!onSubmit && (await onSubmit(values));

                resetForm({ values });
            }
        },
        validationSchema: validateCaseDocument.schema,
    });
    const resetForm = (docToResetTo?: CaseDocument) => {
        caseDocForm.resetForm({
            values: {
                ...docToResetTo,
                description: docToResetTo?.description || '',
                summary: docToResetTo?.summary || '',
            },
        });
    };

    //Logic
    const onTabSelect = (ev: SelectTabEvent, data: SelectTabData) => {
        ev.stopPropagation();
        setSelectedTab(data.value);
    };
    const handleClose = (ev: MouseEvent) => {
        ev.stopPropagation();
        setOpen(false);
    };

    //Icons
    const DocumentBulletListBundle = bundleIcon(DocumentBulletListFilled, DocumentBulletListRegular);
    const NoteIcons = bundleIcon(CommentFilled, CommentRegular);
    const LinkIcons = bundleIcon(LinkFilled, LinkRegular);

    //Effects
    useEffect(() => {
        if (!!selectedDoc) {
            resetForm(selectedDoc);
        } else setOpen(false);
    }, [selectedDoc]);

    return (
        <InlineDrawer
            className={styles.drawer}
            {...restoreFocusSourceAttributes}
            {...otherProps}
            separator
            data-testid='document-drawer'
        >
            <Row height='100%' noGap noWrap>
                <Column width='auto' className={styles.toolColumn}>
                    <TabList
                        selectedValue={selectedTab}
                        size='medium'
                        onTabSelect={onTabSelect}
                        vertical
                        data-testid='document-drawer-tab-list'
                    >
                        <CommonTooltip content='Details'>
                            <Tab
                                className={tabClasses}
                                icon={<DocumentBulletListBundle />}
                                value={TabNames.Info}
                                aria-label={TabNames.Info}
                                data-testid='document-drawer-tab-info'
                            />
                        </CommonTooltip>
                        <CommonTooltip content='Notes'>
                            <Tab
                                className={tabClasses}
                                icon={<NoteIcons />}
                                value={TabNames.Note}
                                aria-label={TabNames.Note}
                                data-testid='document-drawer-tab-notes'
                            />
                        </CommonTooltip>
                        <CommonTooltip content='Links'>
                            <Tab
                                className={tabClasses}
                                icon={<LinkIcons />}
                                value={TabNames.Links}
                                aria-label={TabNames.Links}
                                data-testid='document-drawer-tab-links'
                            />
                        </CommonTooltip>
                    </TabList>
                </Column>
                <Column padding='px-2 pb-1' gap='small'>
                    <DrawerHeader data-testid='document-drawer-header' className={styles.header}>
                        <DrawerHeaderTitle
                            action={
                                <Button
                                    appearance='subtle'
                                    aria-label='Close'
                                    icon={<Dismiss20Regular />}
                                    onClick={handleClose}
                                    data-testid='document-drawer-close-btn'
                                />
                            }
                        >
                            {caseDocForm.values.name}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <Column scroll className={styles.panelDataColumn}>
                            {selectedTab == TabNames.Info && <DocumentInfoPanel caseDocForm={caseDocForm} />}
                            {selectedTab == TabNames.Note && (
                                <DocumentNotesPanel caseId={caseid} selectedDoc={selectedDoc} />
                            )}
                            {selectedTab == TabNames.Links && <DocumentLinksPanel selectedDoc={selectedDoc} />}
                    </Column>
                </Column>
            </Row>
        </InlineDrawer>
    );
};
